import React, { useState } from 'react';
import {
  X as XIcon,
  FileText as FileTextIcon,
  AlertTriangle as AlertTriangleIcon,
  Bot as BotIcon,
  Loader2 as LoaderIcon,
  Sparkles as SparklesIcon
} from 'lucide-react';

const agentColors = {
  gpt: {
    border: 'border-emerald-500/50',
    bg: 'bg-emerald-500/20',
    text: 'text-emerald-400',
    ring: 'ring-emerald-500'
  },
  claude: {
    border: 'border-orange-500/50',
    bg: 'bg-orange-500/20',
    text: 'text-orange-400',
    ring: 'ring-orange-500'
  },
  gemini: {
    border: 'border-blue-500/50',
    bg: 'bg-blue-500/20',
    text: 'text-blue-400',
    ring: 'ring-blue-500'
  }
};

const agentNames = {
  gpt: 'GPT',
  claude: 'Claude',
  gemini: 'Gemini'
};

const SummarizeModal = ({
  isOpen,
  onClose,
  onConfirm,
  loading,
  promptsRemaining,
  currentAgent
}) => {
  const [selectedAgent, setSelectedAgent] = useState(currentAgent || 'gpt');

  if (!isOpen) return null;

  const canProceed = promptsRemaining >= 1;

  const handleConfirm = () => {
    if (canProceed && !loading) {
      onConfirm(selectedAgent);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={!loading ? onClose : undefined}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md glass border border-slate-700/50 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-purple-500/20">
              <FileTextIcon className="h-5 w-5 text-purple-400" />
            </div>
            <h2 className="text-lg font-bold text-white">Summarize & Continue</h2>
          </div>
          {!loading && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-white transition-all"
            >
              <XIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Warning/Info Box */}
          <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-start gap-3">
              <AlertTriangleIcon className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-amber-300 font-medium mb-2">
                  This will create a new thread with a conversation summary.
                </p>
                <ul className="text-amber-200/80 space-y-1 text-xs">
                  <li>- Your current conversation will be summarized</li>
                  <li>- A new thread will be created with the summary</li>
                  <li>- You can continue the conversation in the new thread</li>
                  <li>- <strong>Consumes 1 prompt</strong></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Agent Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3">
              Choose which AI will summarize:
            </label>
            <div className="grid grid-cols-3 gap-2">
              {['gpt', 'claude', 'gemini'].map(agent => (
                <button
                  key={agent}
                  type="button"
                  onClick={() => setSelectedAgent(agent)}
                  disabled={loading}
                  className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                    selectedAgent === agent
                      ? `${agentColors[agent].border} ${agentColors[agent].bg} ring-2 ${agentColors[agent].ring}`
                      : 'border-slate-700 hover:border-slate-600 hover:bg-slate-800/50'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <BotIcon className={`h-5 w-5 ${
                    selectedAgent === agent ? agentColors[agent].text : 'text-slate-400'
                  }`} />
                  <span className={`text-sm font-medium ${
                    selectedAgent === agent ? agentColors[agent].text : 'text-slate-300'
                  }`}>
                    {agentNames[agent]}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Prompts Warning */}
          {!canProceed && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30">
              <p className="text-red-300 text-sm font-medium text-center">
                You need at least 1 prompt remaining to use this feature.
              </p>
            </div>
          )}

          {/* Prompts Info */}
          <div className="text-center">
            <span className={`text-sm ${canProceed ? 'text-slate-400' : 'text-red-400'}`}>
              Prompts remaining: <strong className={canProceed ? 'text-cyan-400' : 'text-red-300'}>{promptsRemaining}</strong>
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 p-5 border-t border-slate-700/50 bg-slate-900/50">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-3 rounded-xl font-medium text-slate-300 hover:bg-slate-700/50 transition-all border border-slate-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canProceed || loading}
            className={`flex-1 px-4 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
              canProceed && !loading
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-lg hover:shadow-purple-500/30'
                : 'bg-slate-700 text-slate-500 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <>
                <LoaderIcon className="h-5 w-5 animate-spin" />
                <span>Summarizing...</span>
              </>
            ) : (
              <>
                <SparklesIcon className="h-5 w-5" />
                <span>Summarize</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SummarizeModal;
